import {
  fetchExternalAwsAccounts,
  fetchExternalAwsResources,
  fetchOperationById,
  selectExternalAwsAccountsForTable,
  selectIsUserOwner,
  selectLatestSyncOpByExternalAwsAccountId,
  syncExternalAwsAccount,
} from "@app/deploy";
import { selectExternalAwsResourceCountByAccountId } from "@app/deploy";
import { selectOrganizationSelectedId } from "@app/organizations";
import {
  useDispatch,
  useLoader,
  useLoaderSuccess,
  useQuery,
  useSelector,
} from "@app/react";
import {
  operationDetailUrl,
  settingsAccountIntegrationsAwsResourcesUrl,
} from "@app/routes";
import { useExternalAwsAccountSyncOpsPoller } from "@app/ui/hooks";
import { useMemo } from "react";
import { Link } from "react-router";
import {
  ActionBar,
  Button,
  ButtonIcon,
  ButtonOrgOwner,
  DescBar,
  EmptyTr,
  FilterBar,
  Group,
  IconAws,
  IconPlusCircle,
  IconRefresh,
  LoadingBar,
  OpStatus,
  TBody,
  THead,
  Table,
  Td,
  Th,
  TitleBar,
  Tooltip,
  Tr,
  tokens,
} from "../shared";

const ownerSyncTooltip =
  "Only organization owners can sync accounts. Ask an org owner to run this sync or grant you owner access.";

const AccountIntegrationRow = ({
  accountId,
  accountName,
  awsAccountId,
  lastDiscoveryAt,
  resourceCount,
  isOwner,
}: {
  accountId: string;
  accountName: string;
  awsAccountId: string;
  lastDiscoveryAt: string;
  resourceCount: number;
  isOwner: boolean;
}) => {
  const dispatch = useDispatch();
  const syncOp = useSelector((s) =>
    selectLatestSyncOpByExternalAwsAccountId(s, { id: accountId }),
  );
  const syncAction = syncExternalAwsAccount({ id: accountId });
  const syncLoader = useLoader(syncAction);
  const syncOpId = syncOp.id || syncLoader.meta?.operationId || "";
  const isSyncing =
    syncLoader.isLoading ||
    syncOp.status === "queued" ||
    syncOp.status === "running";
  const syncDisabled = !isOwner || isSyncing;

  useLoaderSuccess(syncLoader, () => {
    if (syncLoader.meta?.operationId) {
      dispatch(fetchOperationById({ id: syncLoader.meta.operationId }));
    }
  });

  const onSync = () => {
    dispatch(syncAction);
  };

  const syncButton = (
    <ButtonIcon
      variant="white"
      icon={<IconRefresh variant="sm" />}
      aria-label="Sync"
      disabled={syncDisabled}
      onClick={onSync}
    >
      Sync
    </ButtonIcon>
  );

  return (
    <Tr>
      <Td>
        <IconAws className="inline mr-4" />
        <Link
          to={settingsAccountIntegrationsAwsResourcesUrl(accountId)}
          className={tokens.type["table link"]}
        >
          {accountName}
        </Link>
      </Td>
      <Td>{awsAccountId}</Td>
      <Td>
        {isSyncing ? (
          <Group size="xs">
            <div className="flex items-center gap-2">
              <OpStatus status={syncOp.status} />
              <span>Syncing...</span>
            </div>
            {syncOpId ? (
              <Link
                to={operationDetailUrl(syncOpId)}
                className={tokens.type["table link"]}
              >
                View operation
              </Link>
            ) : null}
          </Group>
        ) : (
          <Group size="xs">
            <div>
              {lastDiscoveryAt ? (
                <span>{lastDiscoveryAt}</span>
              ) : (
                <em>Not yet synced</em>
              )}
            </div>
            {syncOpId ? (
              <Link
                to={operationDetailUrl(syncOpId)}
                className={tokens.type["table link"]}
              >
                View operation
              </Link>
            ) : null}
          </Group>
        )}
      </Td>
      <Td>{resourceCount}</Td>
      <Td variant="right">
        <Group variant="horizontal" size="sm" className="justify-end">
          {isOwner ? (
            syncButton
          ) : (
            <Tooltip text={ownerSyncTooltip}>{syncButton}</Tooltip>
          )}
          <Button variant="delete" aria-label="Remove">
            Remove
          </Button>
        </Group>
      </Td>
    </Tr>
  );
};

export const SettingsAccountIntegrationsPage = () => {
  const dispatch = useDispatch();
  const orgId = useSelector(selectOrganizationSelectedId);
  const isOwner = useSelector((s) => selectIsUserOwner(s, { orgId }));
  const { isLoading } = useQuery(fetchExternalAwsAccounts());
  useQuery(fetchExternalAwsResources());
  const accounts = useSelector((s) =>
    selectExternalAwsAccountsForTable(s, { search: "" }),
  );
  const countByAccountId = useSelector(
    selectExternalAwsResourceCountByAccountId,
  );
  const accountIds = useMemo(
    () => accounts.map((a) => a.id).filter(Boolean),
    [accounts],
  );

  useExternalAwsAccountSyncOpsPoller(accountIds);

  const onSyncAll = () => {
    accounts.forEach((account) => {
      dispatch(syncExternalAwsAccount({ id: account.id }));
    });
  };

  return (
    <Group>
      <Group size="sm">
        <div className="flex justify-between">
          <TitleBar description="Connect your external cloud (accounts) so you can easily connect them to your Aptible apps.">
            Account Integrations
          </TitleBar>
          <ActionBar>
            <Group variant="horizontal" size="sm">
              {isOwner ? (
                <Button variant="white" onClick={onSyncAll}>
                  Sync All
                </Button>
              ) : (
                <Tooltip text={ownerSyncTooltip}>
                  <Button variant="white" disabled>
                    Sync All
                  </Button>
                </Tooltip>
              )}
              <Tooltip text="Not implemented yet, use aptible aws_accounts:add cli command for now. See aptible help aws_accounts:add for usage.">
                <ButtonOrgOwner disabled>
                  <IconPlusCircle variant="sm" className="mr-2" />
                  New Account Connection
                </ButtonOrgOwner>
              </Tooltip>
            </Group>
          </ActionBar>
        </div>

        <FilterBar>
          <Group variant="horizontal" size="sm" className="items-center">
            <LoadingBar isLoading={isLoading} />
          </Group>

          <Group variant="horizontal" size="lg" className="items-center mt-1">
            <DescBar>{accounts?.length || "0"} Connected</DescBar>
          </Group>
        </FilterBar>
      </Group>

      <Table>
        <THead>
          <Th>Account</Th>
          <Th>Account ID</Th>
          <Th>Last Synced</Th>
          <Th>Available Resources</Th>
          <Th variant="right">Actions</Th>
        </THead>
        <TBody>
          {accounts?.length === 0 ? (
            <EmptyTr colSpan={5} />
          ) : (
            accounts.map((a) => (
              <AccountIntegrationRow
                key={a.id}
                accountId={a.id}
                accountName={a.accountName}
                awsAccountId={a.awsAccountId}
                lastDiscoveryAt={a.lastDiscoveryAt}
                resourceCount={countByAccountId[a.id] || 0}
                isOwner={isOwner}
              />
            ))
          )}
        </TBody>
      </Table>
    </Group>
  );
};
